import React, { Fragment, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "emotion-theming";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";
import { UserContext } from "wrapper/UserWrapper/context";
import OtherPaymentOffers from "@nykaa/payment-sdk/src/OtherPaymentOffers/mobile";
import { OfferCardLayout } from "@nykaa/payment-sdk/src/OfferCard";
import PaymentOfferInfo from "@nykaa/payment-sdk/src/PaymentOfferInfo/mobile";
import { Text } from "@nykaa/payment-ui/src/Text";
import { Carousel } from "@nykaa/payment-sdk/src/Carousel";
import { usePaymentOfferConfig } from "hooks/usePaymentOfferConfig";
import { OfferLayoutWrapper, OfferHeaderLayoutWrapper } from "./assets/style";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { getPaymentOffersAutoScrollTimer } from "./helper";
import { PaymentOfferListView as PaymentOfferListViewMobile } from "./mobile";


interface IPaymentOffers {
  isVisible: boolean;
  isHeaderVisible?: boolean;
  offersData: any;
  platform: string;
  isApp: boolean;
  visibleOfferBottomSheet?: boolean;
  setVisibleOfferBottomSheet?: () => void;
  setOfferLength?: (length: number) => void;
  savedPaymentPage?: boolean;
  isAutoScroll?: boolean;
  offerForSpecificMode?: string[] | [];
  showCarouselDots?: boolean;
  setLoading?: (isLoading: boolean)=>void;
  isToggleCardAllowed?: boolean;
  showOnlyEligibleOffers?: boolean;
}

const PaymentOffers = ({
  isVisible,
  isHeaderVisible = true,
  offersData,
  platform,
  isApp,
  visibleOfferBottomSheet,
  setVisibleOfferBottomSheet,
  setOfferLength,
  savedPaymentPage = false,
  isAutoScroll = false,
  offerForSpecificMode = [],
  showCarouselDots = false,
  setLoading,
  isToggleCardAllowed = true,
  showOnlyEligibleOffers = false,
}: IPaymentOffers) => {
  const theme = useTheme() as Theme;
  const history = useHistory();
  const { PaymentOfferRevamp } = useContext(ExperimentAndSwitchContext);
  const { userData } = useContext(UserContext);

  const offersConfig = usePaymentOfferConfig({
    offersData,
    deviceType: "MSITE",
    theme,
    platform,
    isApp,
    savedPaymentPage,
    visibleOfferBottomSheet,
    setVisibleOfferBottomSheet,
    PaymentOfferRevamp,
    offerForSpecificMode,
    userData,
    setLoading,
    showOnlyEligibleOffers,
  });

  const allOfferDetails = offersConfig?.paymentOfferConfig?.bankConfig || [];
  const paymentOfferConfig = offersConfig?.paymentOfferConfig;
  const paymentOfferInfoConfig = offersConfig?.paymentOfferInfoConfig;
  const { bottomSheetVisible = false } = paymentOfferInfoConfig;
  const toggleBottomSheet = offersConfig?.cardConfig?.onClick;
  const isClickFromCarousel = offersConfig?.isClickFromCarousel;
  const autoScrollTimer = getPaymentOffersAutoScrollTimer();

  useEffect(() => {
    if (offerForSpecificMode.length) {
      setOfferLength && setOfferLength(allOfferDetails.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOfferDetails.length, offerForSpecificMode.length]);

  if (!isVisible) return null;
  if (!allOfferDetails?.length) return null;

  const offersTitle = getStringFromId(
    VERNACULAR_STRING_IDS.PAYMENT_OFFERS_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.PAYMENT_OFFERS_TITLE
  );
  const offersInfoActionBtnCta = getStringFromId(
    VERNACULAR_STRING_IDS.PAYMENT_OFFERS_ACTION_CTA,
    VERNACULAR_DEFAULT_MESSAGES.PAYMENT_OFFERS_ACTION_CTA
  );

  /**
   * for adding bottomsheet animation.
   */
  const getBottomSheetAnimation = (isBottomSheetVisible: boolean) => {
    if (isClickFromCarousel) {
      return "";
    }

    return isBottomSheetVisible ? "bottom-sheet-animation" : "bottom-sheet-animation-remove";
  };

  /**
   * Payment offers New design
   */
  if (PaymentOfferRevamp?.experimentEnabled) {
    return (
      <>
        <OfferLayoutWrapper className="offerLayoutWrapper">
          {isHeaderVisible && (
            <OfferHeaderLayoutWrapper>
              <Text
                htmlText={offersTitle}
                font={theme.typography.titleSmall}
                color={theme.primaryText}
                theme={theme}
              />
              <Text
                htmlText={offersInfoActionBtnCta}
                font={theme.typography.buttonMedium}
                color={theme.colors.primary}
                onClick={toggleBottomSheet}
                theme={theme}
              />
            </OfferHeaderLayoutWrapper>
          )}
          <Carousel isAutoScroll={isAutoScroll} scrollTimer={autoScrollTimer}>
            {allOfferDetails.map((data: any, offerIndex: number) => {
              const { leadingIcon, offerTitle, title, subTitle, validityConfig, onClick } = data;

              return (
                <Fragment key={`offercard-${offerIndex}`}>
                  <OfferCardLayout
                    keyId={`offercard-${offerIndex}`}
                    leadingIcon={leadingIcon}
                    offerTitle={offerTitle}
                    title={title}
                    subTitleFont={validityConfig?.font}
                    isDisable={validityConfig?.isDisable}
                    subTitle={validityConfig?.message || subTitle}
                    subtitleColor={validityConfig?.color || ""}
                    subTitleClickHandler={validityConfig?.handleRemoveCoupon}
                    currOfferIndex={offerIndex}
                    offersLength={allOfferDetails.length}
                    onClick={isToggleCardAllowed ? () => onClick(true) : () => {}}
                    showCarouselIndex={true}
                    showCarouselDots={showCarouselDots}
                    theme={theme}
                  />
                </Fragment>
              );
            })}
          </Carousel>
        </OfferLayoutWrapper>
        <PaymentOfferListViewMobile
          {...paymentOfferConfig}
          isPaymentOfferRevampEnabled={PaymentOfferRevamp.experimentEnabled}
        />
        <PaymentOfferInfo
          {...{
            ...paymentOfferInfoConfig,
            backIcon: getIcons("", isClickFromCarousel ? ICONS_NAMES.CLEAR : ICONS_NAMES.CHEVRON),
          }}
          history={history as any}
          className={getBottomSheetAnimation(bottomSheetVisible)}
        />
      </>
    );
  }

  /**
   * Payment offers old design
   */
  return (
    <>
      <OtherPaymentOffers {...offersConfig} />
      <PaymentOfferListViewMobile {...paymentOfferConfig} />
      <PaymentOfferInfo
        {...paymentOfferInfoConfig}
        history={history as any}
        className={bottomSheetVisible ? "bottom-sheet-animation" : "bottom-sheet-animation-remove"}
      />
    </>
  );
};

export default PaymentOffers;
